import { useState, useEffect, useRef, KeyboardEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { showSuccess } from "../lib/toast";
import { wishlistApi, mentorshipApi } from "../lib";
import {
  Menu,
  X,
  ChevronDown,
  ArrowRight,
  ChevronRight,
  Sparkles,
  GraduationCap,
  Users,
  BookOpen,
  Mail,
  Home,
  Briefcase,
  Info,
  User,
  Settings,
  LayoutDashboard,
  LogOut,
  Bell,
  Search,
  MessageCircle,
  HelpCircle,
  Star,
  LogIn,
  Heart,
  AlertTriangle,
  Medal,
  Globe,
  Zap,
  Shield,
} from "lucide-react";
import { coursesApi } from "../lib";

const Header = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any>({
    courses: [],
    mentors: [],
    forums: [],
  });
  const [searching, setSearching] = useState(false);
  const [mentorProfileId, setMentorProfileId] = useState<string>("");
  const [wishlistCount, setWishlistCount] = useState(0);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  let hoverTimeout: ReturnType<typeof setTimeout>;

  const formatBadgeCount = (count: number): string => {
    if (count > 99) return "99+";
    if (count > 9) return "9+";
    return count.toString();
  };

  useEffect(() => {
    const fetchWishlistCount = async () => {
      if (!isAuthenticated || !user?._id) return;
      try {
        setWishlistLoading(true);
        const res = await wishlistApi.getMyWishlist({ limit: 1 });
        if (res.success) {
          setWishlistCount(res.pagination.total);
        }
      } catch (err) {
        console.error("Failed to fetch wishlist count:", err);
      } finally {
        setWishlistLoading(false);
      }
    };
    fetchWishlistCount();
  }, [isAuthenticated, user?._id]);

  const getInitials = (name: string) =>
    name
      .split(" ")
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  const userName = user
    ? user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.username || user.email
    : "";
  const userEmail = user?.email || "";

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsProfileOpen(false);
    setIsMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const fetchMentorProfileId = async () => {
      if (user?.role === "mentor" && user?._id) {
        try {
          const res = await mentorshipApi.getMentorByUserId(user._id);
          if (res.success && res.data?._id) {
            setMentorProfileId(res.data._id);
          }
        } catch (err) {
          console.error("Failed to fetch mentor profile:", err);
        }
      }
    };
    fetchMentorProfileId();

    if (user?.role === "student" || user?.role === "mentor") {
      try {
        mentorshipApi.getUnreadCount().then((res) => {
          if (res.success) {
            setUnreadMessages(res.data?.totalUnread || 0);
          }
        });
      } catch (err) {
        console.error("Failed to fetch unread messages:", err);
      }
    }
  }, [user?.role, user?._id]);

  const handleProfileMouseEnter = () => {
    if (hoverTimeout) clearTimeout(hoverTimeout);
    setIsProfileOpen(true);
  };

  const handleProfileMouseLeave = () => {
    hoverTimeout = setTimeout(() => {
      setIsProfileOpen(false);
    }, 200);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        profileRef.current &&
        !profileRef.current.contains(e.target as Node)
      ) {
        setIsProfileOpen(false);
      }
      if (moreRef.current && !moreRef.current.contains(e.target as Node))
        setIsMoreOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setShowLogoutModal(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchUnreadCount = async () => {
      try {
        const res = await fetch("/api/notifications/unread-count", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        });
        if (res.ok) {
          const data = await res.json();
          setUnreadNotifications(data.unreadCount || 0);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!searchQuery || searchQuery.length < 2) {
        setSearchResults({ courses: [], mentors: [], forums: [] });
        return;
      }
      setSearching(true);
      try {
        const data = await coursesApi.globalSearch(
          searchQuery,
          undefined,
          1,
          4,
        );
        setSearchResults({
          courses: data?.courses || [],
          mentors: data?.mentors || [],
          forums: data?.forums || [],
        });
      } catch (err) {
        console.error(err);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const navLinks = [
    { name: "Home", href: "/", icon: Home },
    { name: "Courses", href: "/courses", icon: BookOpen },
    { name: "Mentorship", href: "/mentorship", icon: Users },
    { name: "Community", href: "/forums", icon: MessageCircle },
  ];

  const moreLinks = [
    { name: "About Us", href: "/about", icon: Info },
    { name: "Contact", href: "/contact", icon: Mail },
    { name: "Career", href: "/careers", icon: Briefcase },
  ];

  const profileLinks = [
    { name: "My Profile", href: "/profile", icon: User },
    ...(user?.role === "student"
      ? [{ name: "Badges", href: "/dashboard/badges", icon: Medal }]
      : []),
    ...(user?.role === "student"
      ? [{ name: "My transactions", href: "/dashboard/student/transactions", icon: Zap }]
      : []),
    ...(user?.role === "mentor" && mentorProfileId
      ? [
          {
            name: "Mentor Profile",
            href: `/mentorship/${mentorProfileId}`,
            icon: GraduationCap,
          },
        ]
      : []),
    {
      name: "Dashboard",
      href: `/dashboard/${user?.role || "student"}`,
      icon: LayoutDashboard,
    },
    { name: "Settings", href: "/settings", icon: Settings },
  ];

  const isActive = (href: string) => location.pathname === href;

  const handleLogoutClick = () => {
    setIsProfileOpen(false);
    setIsMobileMenuOpen(false);
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async () => {
    setShowLogoutModal(false);
    await logout();
    showSuccess("Logged out successfully!");
    navigate("/");
  };

  const goToSearchPage = () => {
    setSearchOpen(false);
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
  };

  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      goToSearchPage();
    }
  };

  return (
    <>
      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div
            ref={modalRef}
            className="relative w-full max-w-md overflow-hidden bg-white shadow-2xl rounded-2xl animate-fade-in-up"
          >
            <div className="flex items-center gap-3 p-5 border-b border-red-100 bg-gradient-to-r from-red-50 to-orange-50">
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Confirm Logout
                </h3>
                <p className="text-sm text-gray-500">
                  Are you sure you want to logout?
                </p>
              </div>
            </div>
            <div className="flex gap-3 p-5 pt-0">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleLogoutConfirm}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-100"
            : "bg-white/90 backdrop-blur-sm border-b border-gray-100/50"
        }`}
      >
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0 group">
              <div className="flex items-center gap-0">
                <span className="text-[16px] bg-orange-500 rounded-l-lg font-bold text-white transition-all pl-2 py-0.5">
                  Learn
                </span>
                <span className="text-[16px] font-bold bg-blue-500 text-white transition-all rounded-r-lg pr-2 py-0.5">
                  Hub
                </span>
              </div>
              <span className="ml-2 text-[9px] font-medium text-gray-400 hidden md:inline-block">
                Beta
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="items-center hidden gap-1 md:flex">
              <div className="flex items-center gap-0.5 p-1 bg-gray-50/80 backdrop-blur-sm rounded-full">
                {navLinks.map(({ name, href, icon: Icon }) => (
                  <Link
                    key={name}
                    to={href}
                    className={`px-4 py-2 text-sm font-medium rounded-full flex items-center gap-2 transition-all duration-200 ${
                      isActive(href)
                        ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-md"
                        : "text-gray-600 hover:text-orange-600 hover:bg-white/80"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {name}
                  </Link>
                ))}
                <div className="relative" ref={moreRef}>
                  <button
                    onClick={() => setIsMoreOpen(!isMoreOpen)}
                    className={`px-4 py-2 text-sm font-medium rounded-full flex items-center gap-1.5 transition-all duration-200 ${
                      isMoreOpen
                        ? "bg-white text-orange-600 shadow-sm"
                        : "text-gray-600 hover:text-orange-600 hover:bg-white/80"
                    }`}
                  >
                    More
                    <ChevronDown
                      className={`w-4 h-4 transition-transform duration-200 ${isMoreOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {isMoreOpen && (
                    <div className="absolute right-0 z-50 w-48 py-2 mt-2 bg-white border border-gray-100 shadow-xl rounded-xl animate-fade-in-up">
                      {moreLinks.map(({ name, href, icon: Icon }) => (
                        <Link
                          key={name}
                          to={href}
                          onClick={() => setIsMoreOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                        >
                          <Icon className="w-4 h-4" />
                          {name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </nav>

            {/* Right side actions */}
            <div className="flex items-center flex-shrink-0 gap-1 md:gap-2">
              {/* Search Button - Visible for both logged in and logged out */}
              <button
                onClick={() => setSearchOpen(true)}
                className="p-2 text-gray-500 transition-all duration-200 rounded-xl hover:text-orange-600 hover:bg-orange-50"
              >
                <Search className="w-5 h-5" />
              </button>

              {/* Authenticated User Actions */}
              {isAuthenticated && (
                <>
                  <Link
                    to="/notifications"
                    className="relative p-2 text-gray-500 transition-all duration-200 rounded-xl hover:text-orange-600 hover:bg-orange-50"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadNotifications > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[18px] h-5 px-1.5 bg-gradient-to-r from-red-500 to-red-600 rounded-full text-[9px] text-white flex items-center justify-center font-medium shadow-md">
                        {formatBadgeCount(unreadNotifications)}
                      </span>
                    )}
                  </Link>

                  {(user?.role === "student" || user?.role === "mentor") && (
                    <Link
                      to={
                        user?.role === "mentor"
                          ? "/dashboard/mentor/messages"
                          : "/dashboard/student/messages"
                      }
                      className="relative p-2 text-gray-500 transition-all duration-200 rounded-xl hover:text-orange-600 hover:bg-orange-50"
                    >
                      <MessageCircle className="w-5 h-5" />
                      {unreadMessages > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[18px] h-5 px-1.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full text-[9px] text-white flex items-center justify-center font-medium shadow-md">
                          {formatBadgeCount(unreadMessages)}
                        </span>
                      )}
                    </Link>
                  )}

                  {/* Profile Dropdown */}
                  <div
                    className="relative"
                    ref={profileRef}
                    onMouseEnter={handleProfileMouseEnter}
                    onMouseLeave={handleProfileMouseLeave}
                  >
                    <button
                      onClick={() => setIsProfileOpen(!isProfileOpen)}
                      className="flex items-center gap-2 px-2 py-1.5 ml-1 rounded-full bg-gradient-to-r from-orange-50 to-amber-50 hover:from-orange-100 hover:to-amber-100 transition-all duration-200"
                    >
                      <div className="flex items-center justify-center w-8 h-8 overflow-hidden rounded-full shadow-md bg-gradient-to-br from-orange-500 to-orange-600 ring-2 ring-white">
                        {user?.avatar ? (
                          <img
                            src={user.avatar}
                            alt={userName}
                            className="object-cover w-full h-full"
                          />
                        ) : (
                          <span className="text-xs font-bold text-white">
                            {getInitials(userName)}
                          </span>
                        )}
                      </div>
                      <ChevronDown
                        className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isProfileOpen ? "rotate-180" : ""}`}
                      />
                    </button>

                    {isProfileOpen && (
                      <div className="absolute right-0 z-50 w-64 mt-3 overflow-hidden bg-white border border-gray-100 shadow-xl rounded-xl animate-fade-in-up">
                        <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-white">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {userName}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {userEmail}
                          </p>
                          <div className="flex items-center gap-1 mt-2">
                            <Shield className="w-3 h-3 text-emerald-500" />
                            <span className="text-[10px] text-emerald-600 font-medium">
                              Account Protected
                            </span>
                          </div>
                        </div>
                        <div className="py-1">
                          {profileLinks.map(({ name, href, icon: Icon }) => (
                            <Link
                              key={name}
                              to={href}
                              onClick={() => setIsProfileOpen(false)}
                              className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                            >
                              <Icon className="w-4 h-4" />
                              {name}
                            </Link>
                          ))}
                          <button
                            onClick={handleLogoutClick}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 border-t border-gray-100 mt-1 transition-colors"
                          >
                            <LogOut className="w-4 h-4" />
                            Logout
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Logged Out Actions - NOW VISIBLE ON MOBILE */}
              {!isAuthenticated && (
                <>
                  {/* Desktop view */}
                  <div className="items-center hidden gap-2 md:flex">
                    <Link
                      to="/login"
                      className="px-4 py-2 text-sm font-medium text-gray-600 transition-all duration-200 rounded-xl hover:text-orange-600 hover:bg-orange-50"
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/register"
                      className="px-5 py-2 text-sm font-medium text-white transition-all duration-200 rounded-xl bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-lg hover:shadow-orange-500/25"
                    >
                      Get Started
                      <ArrowRight className="inline w-4 h-4 ml-1" />
                    </Link>
                  </div>

                  {/* Mobile view - Show login/signup buttons */}
                  <div className="flex items-center gap-2 md:hidden">
                    <Link
                      to="/login"
                      className="px-3 py-1.5 text-sm font-medium bg-orange-500  text-white rounded-xl hover:text-orange-50 hover:bg-orange-500 transition-all duration-200"
                    >
                      Sign In
                    </Link>
                  </div>
                </>
              )}

              {/* Mobile Menu Button - Always visible */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-gray-500 transition-all duration-200 rounded-xl md:hidden hover:text-orange-600 hover:bg-orange-50"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Search Modal */}
      {searchOpen && (
        <div className="fixed inset-0 z-[200] flex items-start justify-center pt-20 animate-fade-in">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setSearchOpen(false)}
          />
          <div className="relative w-full max-w-2xl mx-4 overflow-hidden bg-white shadow-2xl rounded-2xl animate-scale-up">
            <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-orange-50/50 to-white">
              <div className="relative">
                <Search className="absolute w-5 h-5 text-gray-400 -translate-y-1/2 left-4 top-1/2" />
                <input
                  type="text"
                  placeholder="Search courses, mentors, forums..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  className="w-full py-3 pl-12 pr-12 text-base transition-all border-gray-200 rounded-xl bg-gray-50 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                  autoFocus
                />
                <button
                  onClick={() => {
                    setSearchOpen(false);
                    setSearchQuery("");
                  }}
                  className="absolute p-1 text-gray-400 transition-colors -translate-y-1/2 right-4 top-1/2 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-[60vh]">
              {/* Search results */}
              <div className="py-3">
                <div className="px-5 py-3">
                  <div className="flex items-center gap-2 mb-3">
                    <BookOpen className="w-4 h-4 text-orange-500" />
                    <p className="text-xs font-semibold tracking-wider text-orange-600 uppercase">
                      Courses
                    </p>
                  </div>
                  <div className="space-y-2">
                    {searchResults.courses.slice(0, 3).map((course: any) => (
                      <Link
                        key={course._id}
                        to={`/course/${course._id}`}
                        onClick={() => setSearchOpen(false)}
                        className="flex items-center gap-3 p-3 transition-all rounded-xl hover:bg-gray-50 group"
                      >
                        <div className="flex-shrink-0 w-12 h-12 overflow-hidden bg-gray-100 rounded-lg">
                          {course.thumbnail ? (
                            <img
                              src={course.thumbnail}
                              alt=""
                              className="object-cover w-full h-full transition-transform group-hover:scale-105"
                            />
                          ) : (
                            <BookOpen className="w-full h-full p-2 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 transition-colors group-hover:text-orange-600">
                            {course.title}
                          </p>
                          <p className="text-xs text-gray-500">
                            {course.category} • {course.difficulty}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400 transition-colors group-hover:text-orange-500" />
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Menu - Modern Slide-in Panel */}
      <div
        className={`fixed top-0 right-0 bottom-0 z-[100] w-full max-w-sm bg-white shadow-2xl transform transition-transform duration-300 ease-out ${
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Menu Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-white">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-0">
                <span className="text-[16px] bg-orange-500 rounded-l-lg font-bold text-white transition-all py-0.5 pl-2">
                  Learn
                </span>
                <span className="text-[16px] font-bold bg-blue-500 text-white transition-all rounded-r-lg py-0.5 pr-2">
                  Hub
                </span>
              </div>
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 text-gray-500 transition-all duration-200 rounded-xl hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* User Banner - Only when authenticated */}
            {isAuthenticated && (
              <div className="flex items-center gap-3 p-5 m-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl">
                <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 overflow-hidden rounded-full shadow-md bg-gradient-to-br from-orange-500 to-orange-600 ring-4 ring-white">
                  {user?.avatar ? (
                    <img
                      src={user.avatar}
                      alt={userName}
                      className="object-cover w-full h-full"
                    />
                  ) : (
                    <span className="text-sm font-bold text-white">
                      {getInitials(userName)}
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {userName}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{userEmail}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <Shield className="w-3 h-3 text-emerald-500" />
                    <span className="text-[9px] text-emerald-600 font-medium">
                      Protected
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Profile Links - Only when authenticated */}
            {isAuthenticated && (
              <div className="py-2">
                {profileLinks.map(({ name, href, icon: Icon }) => (
                  <Link
                    key={name}
                    to={href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-5 py-3 text-sm text-gray-600 transition-colors hover:text-orange-600 hover:bg-orange-50"
                  >
                    <Icon className="w-5 h-5" />
                    {name}
                  </Link>
                ))}
              </div>
            )}

            {/* Navigation Links */}
            <div className="py-2 border-t border-gray-100">
              {navLinks.map(({ name, href, icon: Icon }) => {
                const active = isActive(href);
                return (
                  <Link
                    key={name}
                    to={href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-5 py-3.5 text-sm transition-all duration-200 ${
                      active
                        ? "bg-orange-50 text-orange-600 border-l-4 border-orange-500"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <Icon className="flex-shrink-0 w-5 h-5" />
                    {name}
                  </Link>
                );
              })}
            </div>

            {/* More Links */}
            <div className="py-2 border-t border-gray-100">
              <p className="px-5 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                More
              </p>
              {moreLinks.map(({ name, href, icon: Icon }) => (
                <Link
                  key={name}
                  to={href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-5 py-3 text-sm text-gray-600 transition-colors hover:bg-gray-50"
                >
                  <Icon className="w-5 h-5" />
                  {name}
                </Link>
              ))}
            </div>

            {/* Logout Button - Only when authenticated */}
            {isAuthenticated && (
              <div className="py-2 border-t border-gray-100">
                <button
                  onClick={handleLogoutClick}
                  className="flex items-center w-full gap-3 px-5 py-3 text-sm text-red-600 transition-colors hover:bg-red-50"
                >
                  <LogOut className="w-5 h-5" />
                  Logout
                </button>
              </div>
            )}

            {/* Logged Out CTA - Show Sign In and Create Account buttons */}
            {!isAuthenticated && (
              <div className="p-5 mt-auto border-t border-gray-100">
                <Link
                  to="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full py-3 text-sm font-medium text-center text-white transition-all rounded-xl bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-lg"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full py-3 mt-3 text-sm font-medium text-center text-gray-700 transition-all border border-gray-200 rounded-xl hover:bg-gray-50"
                >
                  Create a free account
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Overlay for mobile menu */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm md:hidden animate-fade-in"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes scale-up {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.2s ease-out;
        }
        .animate-scale-up {
          animation: scale-up 0.2s ease-out;
        }
      `}</style>
    </>
  );
};

export default Header;
